import { Store, StoreType } from '@extTypes/api/customily/user';
import type { CSelectItem } from '@/components/shared/CSelect';
import config from '@/config/config';

export const storeTypeOptions: CSelectItem<StoreType>[] = [
    { value: StoreType.Shopify, label: 'Shopify' },
    { value: StoreType.WooCommerce, label: 'WooCommerce' },
    { value: StoreType.Etsy, label: 'Etsy' },
    { value: StoreType.Amazon, label: 'Amazon' },
    { value: StoreType.Custom, label: 'Custom' },
    { value: StoreType.Shoplazza, label: 'Shoplazza' },
    { value: StoreType.BigCommerce, label: 'BigCommerce' },
    { value: StoreType.Magento, label: 'Magento' },
    { value: StoreType.Walmart, label: 'Walmart' },
    { value: StoreType.Weebly, label: 'Weebly' },
    { value: StoreType.Wix, label: 'Wix' },
    { value: StoreType.Miva, label: 'Miva' }
];

// Installed from their own app store, which also bills every store with its own subscription
const appStoreTypes = new Set([StoreType.Shopify, StoreType.Shoplazza]);

export const isAppStoreType = (type: StoreType) => appStoreTypes.has(type);

export const getAppStoreInstallUrl = (type: StoreType, shop: string) => {
    const encodedShop = encodeURIComponent(shop);
    return type === StoreType.Shoplazza
        ? `${config.APP_SHOPIFY_BASE_URL}/shoplazza/auth?shop=${encodedShop}`
        : `${config.SHOPIFY_PRODUCT_PERSONALIZER_BASE_URL}/auth/login?shop=${encodedShop}`;
};

// Their Customily subscription covers all the user stores of the type, every store of the other types needs its own
const subscriptionSharedTypes = new Set([StoreType.WooCommerce, StoreType.Etsy, StoreType.Amazon]);

export const sharesSubscriptionByType = (type: StoreType) => subscriptionSharedTypes.has(type);

const DEFAULT_SUBSCRIPTION_MONTHLY_PRICE = 49;
const subscriptionMonthlyPrices: Partial<Record<StoreType, number>> = {
    [StoreType.Custom]: 299
};

export const getSubscriptionMonthlyPrice = (type: StoreType) =>
    subscriptionMonthlyPrices[type] ?? DEFAULT_SUBSCRIPTION_MONTHLY_PRICE;

/**
 * Whether adding a store of the type needs a new Customily subscription. Mirrors the billing inheritance of the core
 *  StoreBL, which has the final say.
 */
export const requiresStoreSubscription = (type: StoreType, stores: Store[]) =>
    !isAppStoreType(type) && !(sharesSubscriptionByType(type) && stores.some((s) => s.Type === type));

export const getStoreType = (shop: string, stores: Store[]) => stores.find((s) => s.Url === shop)?.Type;

/**
 * First store the user can actually work with. Picking stores[0] blindly can select a disabled or
 *  uninstalled store, which the server rejects with "No shop <url> for this user".
 */
export const firstAvailableStore = (stores: Store[]) => stores.find((s) => !s.Disabled && s.AppInstalled);

export const isStoreOfType = (shop: string, stores: Store[], storeType: StoreType) => {
    return stores.some((s) => s.Url === shop && s.Type === storeType);
};

export const isStoreOfTypes = (shop: string, stores: Store[], storeType: StoreType[]) => {
    return stores.some((s) => s.Url === shop && storeType.includes(s.Type));
};

/** Platforms without a theme to inject into: Customily hosts the personalization page itself. */
export const STANDALONE_STORE_TYPES: StoreType[] = [
    StoreType.Etsy,
    StoreType.Custom,
    StoreType.Amazon,
    StoreType.TikTok
];

/** Standalone platforms whose personalization is tied to an order placed in the marketplace. */
export const ORDER_STANDALONE_STORE_TYPES: StoreType[] = [StoreType.Etsy, StoreType.Amazon, StoreType.TikTok];

export const isStandaloneStoreType = (storeType?: StoreType) =>
    storeType !== undefined && STANDALONE_STORE_TYPES.includes(storeType);

export const isStandaloneStore = (shop: string, stores: Store[]) =>
    isStoreOfTypes(shop, stores, STANDALONE_STORE_TYPES);

export const isOrderStandaloneStore = (shop: string, stores: Store[]) => {
    return isStoreOfTypes(shop, stores, ORDER_STANDALONE_STORE_TYPES);
};

export const isWooCommerceStore = (shop: string, stores: Store[]) => {
    const store = stores.find((s) => {
        return s.Url === shop;
    });
    return store && store.Type === StoreType.WooCommerce;
};

export const hasWooCommerceStores = (stores: Store[]) => {
    return stores.some((s) => s.Type === StoreType.WooCommerce);
};

export const isEtsyStore = (shop: string, stores: Store[]) => {
    return isStoreOfType(shop, stores, StoreType.Etsy);
};

export const isTikTokStore = (shop: string, stores: Store[]) => {
    return isStoreOfType(shop, stores, StoreType.TikTok);
};

export const isCustomStore = (shop: string, stores: Store[]) => {
    return isStoreOfType(shop, stores, StoreType.Custom);
};

export const isShoplazzaStore = (shop: string, stores: Store[]) => {
    return isStoreOfType(shop, stores, StoreType.Shoplazza);
};

export const isAmazonStore = (shop: string, stores: Store[]) => {
    return isStoreOfType(shop, stores, StoreType.Amazon);
};

export const isShopify = (shop: string, stores: Store[]) => {
    return isStoreOfType(shop, stores, StoreType.Shopify);
};
